<template>
    <Popover
        class="Expandable"
        self-contained
        :portal-target="menuConfig.portalTarget || defaultPortalTarget"
        :teleport="menuConfig.teleport || false"
        :position-x="menuConfig.positionX || 'start'"
        :position-y="menuConfig.positionY || 'bottom'"
        :offset-x="menuConfig.offsetX || 0"
        :offset-y="menuConfig.offsetY || 0"
        :auto-flip="menuConfig.autoFlip || null"
        :flip-estimated-height="menuConfig.flipEstimatedHeight || 300"
        :flip-estimated-width="menuConfig.flipEstimatedWidth || 400"
        @toggle-open="$emit('open')"
        @toggle-close="$emit('close')"
    >
        <template #base="{ toggle, isOpen }">
            <slot name="trigger" :toggle="toggle" :is-open="isOpen">
                <NewButton
                    :color="triggerConfig.color || 'primary'"
                    :variant="triggerConfig.variant || 'fill'"
                    :size="triggerConfig.size || 'default'"
                    :disabled="disabled || false"
                    @click="!disabled && toggle()"
                >
                    <EidIcon v-if="icon" :icon-src="icon" />
                    <span v-if="label">{{ label }}</span>
                </NewButton>
            </slot>
        </template>

        <template #default="{ close }">
            <div class="Expandable__menu">
                <slot :close="close">
                    <ExpandableItem
                        v-for="(item, index) in menuItems"
                        :key="index"
                        :label="item.label"
                        :icon="item.icon || null"
                        :disabled="item.disabled || false"
                        @click="onItemClick(item, close)"
                    />
                </slot>
            </div>
        </template>
    </Popover>
</template>

<style lang="scss">
    .Expandable {
        &__menu {
            background: #ffffff;
            border: 1px solid var(--eidLightGray-25);
            border-radius: 8.75px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            min-width: 200px;
            padding: 8px 4px;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import Popover from '@evidentid/dashboard-commons/components/Popover/Popover.vue';

    import ExpandableItem from '@/components/expandable-item/ExpandableItem.vue';
    import NewButton from '@/components/new-button/NewButton.vue';
    import { ExpandableMenuItem } from '@/models/ExpandableMenuItemConfig.model';

    interface ExpandableTriggerConfig {
        color?: string;
        variant?: string;
        size?: string;
    }

    interface ExpandableMenuConfig {
        positionX?: 'start' | 'end';
        positionY?: 'top' | 'bottom';
        offsetX?: number;
        offsetY?: number;
        autoFlip?: boolean | 'x' | 'y' | 'xy' | null;
        flipEstimatedHeight?: number;
        flipEstimatedWidth?: number;
        teleport?: boolean;
        portalTarget?: string;
    }

    export default defineComponent({
        name: 'Expandable',
        components: { Popover, NewButton, EidIcon, ExpandableItem },
        props: {
            label: {
                type: String as PropType<string>,
                default: null,
            },
            icon: {
                type: [ String, Object ] as PropType<string | IconDefinition | null>,
                default: null,
            },
            disabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            // Trigger button props (used when no #trigger slot is provided)
            triggerConfig: {
                type: Object as PropType<ExpandableTriggerConfig>,
                default: (): ExpandableTriggerConfig => ({}),
            },
            // Menu items config (used when no #default slot is provided)
            menuItems: {
                type: Array as PropType<ExpandableMenuItem[]>,
                default: (): ExpandableMenuItem[] => [],
            },
            menuConfig: {
                type: Object as PropType<ExpandableMenuConfig>,
                default: (): ExpandableMenuConfig => ({}),
            },
        },
        emits: [ 'close', 'open' ],
        data() {
            return {
                defaultPortalTarget: 'default-popover-target',
            };
        },
        methods: {
            onItemClick(item: ExpandableMenuItem, close: () => void): void {
                if (!item.disabled) {
                    item.action();
                    close();
                }
            },
        },
    });
</script>
